import { usePage } from '@inertiajs/vue3';
import * as Sentry from '@sentry/vue';

export function debounce(fn, wait) {
    let timer;
    return function(...args) {
        if (timer) {
            clearTimeout(timer); // clear any pre-existing timer
        }

        const context = this; // get the current context
        timer = setTimeout(() => {
            fn.apply(context, args); // call the function if time expires
        }, wait);
    };
}

export function hasPermission (permission) {
    const page = usePage();

    if (! page.props.auth) {
        return true;
    }

    if (page.props.isModerator) {
        return true;
    }

    if ('string' === typeof permission) {
        return page.props.permissions.includes(permission);
    }

    return page.props.permissions.some(p => permission.includes(p));
}

export function cleanJiraMarkdown (markdown, removeLinks = false) {
    if (! markdown) {
        return '';
    }

    // Jira Links: [Link|URL] -> [Link](URL)
    if (removeLinks) {
        markdown = markdown.replace(/\[([^|\]]+)\|([^\]]+)\]/g, '');
    } else {
        markdown = markdown.replace(/\[([^|\]]+)\|([^\]]+)\]/g, '[Link]($2)');
    }

    // Normalize line endings to \n
    markdown = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Jira bold and italic: *text* and _text_
    markdown = markdown.replace(/\*(.*?)\*/g, '**$1**');
    markdown = markdown.replace(/_(.*?)_/g, '*$1*');

    // Jira lists: * item -> - item
    markdown = markdown.replace(/^([ \t]*)#[ \t]+/gm, '$1- ');

    // Headers: ... -> ####
    markdown = markdown.replace(/h1\./g, '#### ');
    markdown = markdown.replace(/h2\./g, '#### ');

    // Jira images: !filename.ext|width=xxx,height=yyy,alt="Some alt text"!
    // Markdown: ![Some alt text](filename.ext)
    markdown = markdown.replace(/!([^|!]+)(?:\|[^!]+)?!/g, (match, filePart) => {
        const altMatch = match.match(/alt="([^"]*)"/);
        const altText = altMatch && altMatch[1] ? altMatch[1] : filePart;

        return `![${altText}](${filePart})`;
    });

    // Single line breaks
    markdown = markdown.replace(/\n/g, '  \n');

    return markdown;
}

export function truncateFileName(fileName, maxLength = 30) {
    if (! fileName || maxLength >= fileName.length) {
        return fileName;
    }

    const lastDotIndex = fileName.lastIndexOf('.');

    if (-1 === lastDotIndex) {
        return `${fileName.substring(0, maxLength - 3)}...`;
    }

    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    const ellipsisLength = 3;
    const availableForName = maxLength - extension.length - ellipsisLength;

    if (0 >= availableForName) {
        return `...${extension}`;
    }

    const splicedName = name.substring(0, availableForName);

    return `${splicedName}...${extension}`;
}

export function safeGetLocal(key, fallback = null) {
    try {
        return localStorage.getItem(key) ?? fallback;
    } catch (e) {
        Sentry.captureException(e);

        return fallback;
    }
}

export function safeSetLocal(key, value) {
    try {
        localStorage.setItem(key, value);

        return true;
    } catch (e) {
        Sentry.captureException(e);

        return false;
    }
}

export function safeRemoveLocal(key) {
    try {
        localStorage.removeItem(key);

        return true;
    } catch (e) {
        Sentry.captureException(e);

        return false;
    }
}

const DEFAULT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function safeGetCookie(name, fallback = null) {
    try {
        if ('undefined' === typeof document) {
            return fallback;
        }

        const encodedName = encodeURIComponent(name);
        const cookie = document.cookie
            .split('; ')
            .find(entry => entry.startsWith(`${encodedName}=`));

        if (! cookie) {
            return fallback;
        }

        return decodeURIComponent(cookie.slice(encodedName.length + 1));
    } catch (e) {
        Sentry.captureException(e);

        return fallback;
    }
}

export function safeSetCookie(name, value, maxAge = DEFAULT_COOKIE_MAX_AGE) {
    try {
        if ('undefined' === typeof document) {
            return false;
        }

        const attributes = [
            `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`,
            'path=/',
            `max-age=${maxAge}`,
            'samesite=lax',
        ];

        if ('undefined' !== typeof window && 'https:' === window.location.protocol) {
            attributes.push('secure');
        }

        document.cookie = attributes.join('; ');

        return true;
    } catch (e) {
        Sentry.captureException(e);

        return false;
    }
}

export function safeRemoveCookie(name) {
    try {
        if ('undefined' === typeof document) {
            return false;
        }

        document.cookie = [
            `${encodeURIComponent(name)}=`,
            'path=/',
            'max-age=0',
            'samesite=lax',
        ].join('; ');

        return true;
    } catch (e) {
        Sentry.captureException(e);

        return false;
    }
}

const UI_PREFERENCE_COOKIE_NAMES = {
    issuesView: 'issuesView',
    theme: 'theme',
    timestampMode: 'timestamp-mode',
};

const UI_PREFERENCE_ALLOWED_VALUES = {
    issuesView: ['left', 'right'],
    theme: ['light', 'dark'],
    timestampMode: ['local', 'utc'],
};

function normalizeUiPreference(key, value, fallback = null) {
    if (undefined === value || null === value) {
        return fallback;
    }

    const allowedValues = UI_PREFERENCE_ALLOWED_VALUES[key];

    if (allowedValues && ! allowedValues.includes(value)) {
        return fallback;
    }

    return value;
}

export function getUiPreference(key, fallback = null) {
    const pageValue = usePage().props.uiPreferences?.[key];

    if (undefined !== pageValue && null !== pageValue) {
        return normalizeUiPreference(key, pageValue, fallback);
    }

    return normalizeUiPreference(
        key,
        safeGetCookie(UI_PREFERENCE_COOKIE_NAMES[key] ?? key, null),
        fallback,
    );
}
